// ─────────────────────────────────────────────────────────────────────────────
// SECURITY FIX #8: Client-Side Rate Limiting
//
// Why client-side? Because this app has no dedicated backend server.
// All API calls go directly to Supabase. The Supabase dashboard lets you
// set server-side rate limits on Auth endpoints (recommended), but we also
// add client-side guards to:
//   1. Prevent accidental UI-triggered spam (double-clicks, fast retries)
//   2. Protect expensive operations (email sends, RPC calls)
//   3. Give users clear feedback before a server-level 429 hits
//
// This uses a simple fixed-window counter stored in memory (per tab).
// For persistent cross-tab limits, use localStorage with TTL.
// ─────────────────────────────────────────────────────────────────────────────

const windows = new Map(); // key → { count, resetAt }

/**
 * Check if an action is allowed under the rate limit.
 *
 * @param {string}  key          - Unique identifier for the action (e.g. 'auth:login')
 * @param {number}  maxRequests  - Max calls allowed in the window
 * @param {number}  windowMs     - Window duration in milliseconds
 * @returns {{ allowed: boolean, remaining: number, resetIn: number }}
 */
export const checkRateLimit = (key, maxRequests, windowMs) => {
  const now = Date.now();
  const entry = windows.get(key);

  if (!entry || now >= entry.resetAt) {
    // New window
    windows.set(key, { count: 1, resetAt: now + windowMs });
    return { allowed: true, remaining: maxRequests - 1, resetIn: windowMs };
  }

  if (entry.count >= maxRequests) {
    return {
      allowed:   false,
      remaining: 0,
      resetIn:   entry.resetAt - now,
    };
  }

  entry.count += 1;
  return {
    allowed:   true,
    remaining: maxRequests - entry.count,
    resetIn:   entry.resetAt - now,
  };
};

// ── Pre-configured limiters for common actions ────────────────────────────────

/** Login attempts: 5 per 60 seconds */
export const checkLoginLimit = () =>
  checkRateLimit('auth:login', 5, 60_000);

/** Sign-up attempts: 3 per 5 minutes */
export const checkSignupLimit = () =>
  checkRateLimit('auth:signup', 3, 300_000);

/** Password reset / credential email: 2 per 5 minutes */
export const checkPasswordResetLimit = (email) =>
  checkRateLimit(`auth:reset:${email}`, 2, 300_000);

/** Application submissions: 1 per 10 minutes */
export const checkApplicationLimit = () =>
  checkRateLimit('app:submit', 1, 600_000);

/** Admin approval actions: 20 per minute */
export const checkAdminActionLimit = () =>
  checkRateLimit('admin:action', 20, 60_000);

/** Generic RPC calls: 30 per minute */
export const checkRpcLimit = (rpcName) =>
  checkRateLimit(`rpc:${rpcName}`, 30, 60_000);

/**
 * Returns a human-readable "try again in X seconds/minutes" string.
 */
export const formatResetTime = (resetInMs) => {
  const sec = Math.ceil(resetInMs / 1000);
  if (sec < 60) return `${sec} second${sec !== 1 ? 's' : ''}`;
  const min = Math.ceil(sec / 60);
  return `${min} minute${min !== 1 ? 's' : ''}`;
};